// frame: pVxtI @ ui/dogdisk-user.pen 弹框-导出账号密码
// 本地 Canvas 渲染全部已保存账号的明文账号+密码为一张卡片,点击保存为 PNG(明文不出浏览器)。
import { useEffect, useMemo, useRef } from 'react'
import { Download, ShieldAlert } from 'lucide-react'
import type { SavedAccount } from '@dogdisk/shared'
import { buildExportModel, drawAccountExport } from '../../_lib/account-export'

function todayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

export type ExportAccountsModalProps = {
  open: boolean
  onClose: () => void
  accounts: SavedAccount[]
}

export function ExportAccountsModal({ open, onClose, accounts }: ExportAccountsModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const dateStr = useMemo(() => todayStr(), [open])
  const model = useMemo(() => buildExportModel(accounts, dateStr), [accounts, dateStr])

  useEffect(() => {
    if (!open) return
    const canvas = canvasRef.current
    if (!canvas) return
    const render = () => drawAccountExport(canvas, model)
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(render).catch(render)
    } else {
      render()
    }
  }, [open, model])

  if (!open) return null

  const onSave = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.toBlob((blob) => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `account-${dateStr}.png`
      a.click()
      URL.revokeObjectURL(url)
    }, 'image/png')
  }

  return (
    <div
      data-frame-id="pVxtI"
      data-testid="pVxtI"
      role="dialog"
      aria-modal="true"
      aria-label="导出账号密码"
      className="fixed inset-0 z-50 flex items-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="mx-auto w-full max-w-[402px] rounded-t-3xl border border-[#E0E0E0] bg-white"
        style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center pt-2 pb-3">
          <span className="h-1 w-10 rounded-full bg-[#E5E7EB]" />
        </div>
        <header className="px-6 pb-2 text-center">
          <h2 className="text-[22px] font-bold text-[#1A1A1A]">导出账号密码</h2>
          <p className="mt-1 text-[14px] text-[#9CA3AF]">以下为已保存账号的明文信息，请妥善保管</p>
        </header>
        <div className="px-6 py-2">
          <canvas
            ref={canvasRef}
            data-testid="pVxtI-canvas"
            aria-label={`${accounts.length} 个账号的明文导出预览`}
            className="w-full"
          />
        </div>
        <div className="flex flex-col gap-3 px-6 pt-2">
          <div className="flex items-center gap-2 rounded-[10px] bg-[#FEF3F2] px-3 py-2.5">
            <ShieldAlert className="h-4 w-4 shrink-0 text-[#EF4444]" aria-hidden="true" />
            <span className="text-[13px] font-medium text-[#B42318]">图片包含明文密码，请勿外传</span>
          </div>
          <button
            type="button"
            data-testid="pVxtI-save"
            onClick={onSave}
            className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[#19caad] text-[18px] font-semibold text-white active:bg-[#14a18b]"
          >
            <Download className="h-5 w-5" aria-hidden="true" />
            保存图片
          </button>
        </div>
      </div>
    </div>
  )
}
